/**
 * 用户端图片上传（与 Apipost「用户端 / 图片上传」保持一致）
 *
 * 与 Apipost「用户端 / 图片上传」一致：
 *   POST {base}/home/upload/image，form-data：file（必填）、folder（存储目录，文档示例 home/images）
 *   Header：X-Requested-With: XMLHttpRequest；Authorization: Bearer <token>（有 token 时）
 * 可覆盖项见 .env：VITE_UPLOAD_IMAGE_PATH、VITE_UPLOAD_FILE_FIELD、VITE_UPLOAD_IMAGE_FOLDER
 */

/**
 * 用户端音频上传（与 Apipost「用户端 / 音频上传」保持一致）
 *
 * 与 Apipost「用户端 / 音频上传」一致：
 *   POST {base}/home/upload/audio，form-data：file（必填）、folder（必填，文档示例 home/audio）
 *   Header：X-Requested-With: XMLHttpRequest；Authorization: Bearer <token>（有 token 时）
 */
import axios from "axios";
import { useUserStore } from "@/store/useUserStore";
import { toastError } from "@/utils/toast";

function apiBase(): string {
  return (import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/$/, "");
}

function uploadPath(): string {
  const p = import.meta.env.VITE_UPLOAD_IMAGE_PATH || "/home/upload/image";
  return p.startsWith("/") ? p : `/${p}`;
}

function fileFieldName(): string {
  return import.meta.env.VITE_UPLOAD_FILE_FIELD || "file";
}

/** Apipost 示例目录；设为空字符串可不发 folder 字段 */
function uploadFolderParam(): string {
  const v = import.meta.env.VITE_UPLOAD_IMAGE_FOLDER;
  if (v === "") return "";
  if (typeof v === "string" && v.length) return v;
  return "home/images";
}

function isResponseOk(body: Record<string, unknown>): boolean {
  if (body.code === 200) return true;
  if (body.status === 200) return true;
  return false;
}

function pickImageUrl(body: Record<string, unknown>): string | null {
  const d = body.data;
  if (typeof d === "string" && (d.startsWith("http") || d.startsWith("/"))) {
    return resolveMaybeRelative(d);
  }
  if (d && typeof d === "object" && !Array.isArray(d)) {
    const o = d as Record<string, unknown>;
    const u = o.url ?? o.image ?? o.path ?? o.file ?? o.src;
    if (typeof u === "string" && u.length) return resolveMaybeRelative(u);
  }
  if (Array.isArray(d) && d.length > 0) {
    const first = d[0] as Record<string, unknown>;
    const u = first?.url ?? first?.image;
    if (typeof u === "string") return resolveMaybeRelative(u);
  }
  if (typeof body.url === "string") return resolveMaybeRelative(body.url);
  return null;
}

/** 相对路径补全为可访问 URL（域名取自 API 根） */
function resolveMaybeRelative(pathOrUrl: string): string {
  if (pathOrUrl.startsWith("http://") || pathOrUrl.startsWith("https://")) {
    return pathOrUrl;
  }
  const root = apiBase().replace(/\/api\/?$/i, "");
  if (pathOrUrl.startsWith("/")) return `${root}${pathOrUrl}`;
  return pathOrUrl;
}

/**
 * multipart 上传单图，返回可访问的图片地址（供订单出行人证件等字段使用）
 */
export async function uploadImage(file: File): Promise<string> {
  const url = `${apiBase()}${uploadPath()}`;
  const fd = new FormData();
  fd.append(fileFieldName(), file);
  const folder = uploadFolderParam();
  if (folder) fd.append("folder", folder);

  const { token, tokenType } = useUserStore.getState();
  const headers: Record<string, string> = {
    "X-Requested-With": "XMLHttpRequest",
  };
  if (token) headers.Authorization = `${tokenType} ${token}`;

  let res;
  try {
    res = await axios.post<Record<string, unknown>>(url, fd, {
      timeout: 120000,
      headers,
    });
  } catch (e: unknown) {
    const ax = e as { response?: { data?: { message?: string; msg?: string } } };
    const msg =
      ax.response?.data?.message ||
      ax.response?.data?.msg ||
      (e instanceof Error ? e.message : null) ||
      "图片上传失败";
    toastError(msg);
    throw e;
  }

  const body = res.data;
  if (!body || typeof body !== "object") {
    toastError("图片上传响应异常");
    throw new Error("invalid response");
  }

  if (!isResponseOk(body)) {
    const msg =
      (typeof body.message === "string" && body.message) ||
      (typeof body.msg === "string" && body.msg) ||
      "图片上传失败";
    toastError(msg);
    throw new Error(msg);
  }

  const imageUrl = pickImageUrl(body);
  if (!imageUrl) {
    toastError("上传成功但未返回图片地址，请检查接口或联系后端");
    throw new Error("no image url in response");
  }
  return imageUrl;
}

/** 音频上传接口路径（与 Apipost 一致：home/upload/audio） */
function audioUploadPath(): string {
  const p = import.meta.env.VITE_UPLOAD_AUDIO_PATH || "/home/upload/audio";
  return p.startsWith("/") ? p : `/${p}`;
}

/** 音频上传默认存储目录（Apipost 文档示例：home/audio） */
function audioFolderParam(): string {
  const v = import.meta.env.VITE_UPLOAD_AUDIO_FOLDER;
  if (typeof v === "string" && v.length) return v;
  return "home/audio";
}

/**
 * multipart 上传音频文件，返回可访问的音频地址
 * 支持格式：mp3/wav/ogg/m4a/flac
 */
export async function uploadAudio(file: File): Promise<string> {
  const url = `${apiBase()}${audioUploadPath()}`;
  const fd = new FormData();
  fd.append("file", file);
  fd.append("folder", audioFolderParam());

  const { token, tokenType } = useUserStore.getState();
  const headers: Record<string, string> = {
    "X-Requested-With": "XMLHttpRequest",
  };
  if (token) headers.Authorization = `${tokenType} ${token}`;

  let res;
  try {
    res = await axios.post<Record<string, unknown>>(url, fd, {
      timeout: 120000,
      headers,
    });
  } catch (e: unknown) {
    const ax = e as { response?: { data?: { message?: string; msg?: string } } };
    const msg =
      ax.response?.data?.message ||
      ax.response?.data?.msg ||
      (e instanceof Error ? e.message : null) ||
      "音频上传失败";
    toastError(msg);
    throw e;
  }

  const body = res.data;
  if (!body || typeof body !== "object") {
    toastError("音频上传响应异常");
    throw new Error("invalid response");
  }

  if (!isResponseOk(body)) {
    const msg =
      (typeof body.message === "string" && body.message) ||
      (typeof body.msg === "string" && body.msg) ||
      "音频上传失败";
    toastError(msg);
    throw new Error(msg);
  }

  // 复用图片 URL 提取逻辑，音频返回结构类似
  const audioUrl = pickImageUrl(body);
  if (!audioUrl) {
    toastError("上传成功但未返回音频地址，请检查接口或联系后端");
    throw new Error("no audio url in response");
  }
  return audioUrl;
}
